import * as React from "react";
import { ItemRenderer, ItemRendererProps } from "@blueprintjs/select";
import { MenuItem, MenuItemProps } from "@blueprintjs/core";
import { useCallback } from "react";
import { IGroup } from "../../../api/admin";
import { MultiSelect } from "./MultiSelect";
import { useSelectedItems } from "../../../hooks/useSelectedItems";
import { cappedItemListRenderer } from "./cappedItemListRenderer";

const filterGroup = (query: string, group: IGroup) =>
  group.name.toLowerCase().includes(query.toLowerCase());

const areGroupsEqual = (group1: IGroup, group2: IGroup) =>
  group1.id === group2.id;

const renderTag = (group: IGroup) => group.name;

interface FolderGroupMultiSelectProps {
  selectedGroupIds: number[];
  groups: IGroup[];
  onGroupChange: (groupIds: number[]) => void;
}

export function FolderGroupMultiSelect({
  selectedGroupIds,
  groups,
  onGroupChange,
}: FolderGroupMultiSelectProps) {
  const { idSet: selectedGroupIdSet, selectedItems } = useSelectedItems(
    selectedGroupIds,
    groups
  );

  const handleGroupRemove = useCallback(
    (group: IGroup) => {
      const newGroupIds = selectedGroupIds.filter(
        (id: number) => id !== group.id
      );
      onGroupChange(newGroupIds);
    },
    [selectedGroupIds, onGroupChange]
  );

  const handleGroupSelect = useCallback(
    (group: IGroup) => {
      if (selectedGroupIdSet.has(group.id)) {
        handleGroupRemove(group);
      } else {
        const newGroupIds = [...selectedGroupIds, group.id];
        onGroupChange(newGroupIds);
      }
    },
    [selectedGroupIds, selectedGroupIdSet, onGroupChange, handleGroupRemove]
  );

  const renderGroup: ItemRenderer<IGroup> = useCallback(
    (group, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const isSelected = selectedGroupIdSet.has(group.id);

      return (
        <MenuItem
          {...getGroupItemProps(group, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={group.name}
        />
      );
    },
    [selectedGroupIdSet]
  );

  return (
    <MultiSelect
      items={groups}
      selectedItems={selectedItems}
      itemRenderer={renderGroup}
      itemListRenderer={cappedItemListRenderer}
      itemPredicate={filterGroup}
      itemsEqual={areGroupsEqual}
      onItemSelect={handleGroupSelect}
      onRemove={handleGroupRemove}
      placeholder="Select groups..."
      disabled={!groups || groups.length === 0}
      popoverProps={{
        minimal: true,
        matchTargetWidth: true,
      }}
      tagRenderer={renderTag}
      tagInputProps={{
        tagProps: {
          minimal: false,
        },
      }}
      resetOnSelect={true}
    />
  );
}

export function getGroupItemProps(
  group: IGroup,
  { handleClick, handleFocus, modifiers, query, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: group.id,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    text: group.name,
    selected: isSelected,
  };
}
